import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { chapterAPI, novelAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const ChapterForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [novels, setNovels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  
  const [formData, setFormData] = useState({
    novel: '',
    chapterNumber: 1,
    title: '',
    content: '',
    translatorNotes: ''
  });

  useEffect(() => {
    fetchNovels();
    if (isEdit) {
      fetchChapter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNovels = async () => {
    try {
      const response = await novelAPI.getAll({ limit: 100 });
      setNovels(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChapter = async () => {
    try {
      const response = await chapterAPI.getById(id);
      const chapter = response.data;
      setFormData({
        novel: chapter.novel?._id || chapter.novel || '',
        chapterNumber: chapter.chapterNumber || 1,
        title: chapter.title || '',
        content: chapter.content || '',
        translatorNotes: chapter.translatorNotes || ''
      });
    } catch (error) {
      toast.error('Failed to load chapter');
      navigate('/admin/chapters');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.novel) {
      toast.error('Please select a novel');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter chapter content');
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit) {
        await chapterAPI.update(formData.novel, id, formData);
        toast.success('Chapter updated successfully');
      } else {
        await chapterAPI.create(formData.novel, formData);
        toast.success('Chapter created successfully');
      }
      navigate('/admin/chapters');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="chapter-form-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            onClick={() => navigate('/admin/chapters')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1>{isEdit ? 'Edit Chapter' : 'Add New Chapter'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-row">
          <div className="form-group">
            <label>Novel *</label>
            <select
              value={formData.novel}
              onChange={(e) => setFormData({ ...formData, novel: e.target.value })}
              disabled={isEdit}
            >
              <option value="">-- Select Novel --</option>
              {novels.map((novel) => (
                <option key={novel._id} value={novel._id}>{novel.title}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Chapter Number</label>
            <input
              type="number"
              value={formData.chapterNumber}
              onChange={(e) => setFormData({ ...formData, chapterNumber: parseInt(e.target.value) || 1 })}
              min="1"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Chapter Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Chapter 1: The Beginning"
          />
        </div>

        <div className="form-group">
          <label>Content *</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Enter chapter content..."
            rows={20}
            style={{ fontFamily: 'var(--font-reading)', lineHeight: '1.8' }}
          />
        </div>

        <div className="form-group">
          <label>Translator Notes (optional)</label>
          <textarea
            value={formData.translatorNotes}
            onChange={(e) => setFormData({ ...formData, translatorNotes: e.target.value })}
            placeholder="Notes for readers..."
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : (
              <>
                <Save size={18} />
                {isEdit ? 'Update Chapter' : 'Create Chapter'}
              </>
            )}
          </button>
          <button type="button" className="btn-cancel" onClick={() => navigate('/admin/chapters')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChapterForm;
